import { Box, Button, Flex, Grid, GridItem, Input, Text, Textarea } from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { ActionFab } from '../../../components/ActionButton'
import { BackLink } from '../../../components/BackLink'
import { FILE_TYPES, useFilePicker } from '../../../components/FileInput'
import { FormField } from '../../../components/FormField'
import { Icon } from '../../../components/Icon'
import { LoadingBlock } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import { FormSelect } from '../../../components/Select'
import { useAppToast } from '../../../helpers'
import { IAbraMidiaFileRaw } from '../../../interfaces/AbraMidia'
import AbraMidiaService from '../../../services/AbraMidia/AbraMidiaService'

interface IForm {
  name: string
  note: string
}

interface IUploadedFile {
  id: string
  url: string
  name: string
}

// O limite por arquivo que a tela sempre anunciou — e que até aqui nada conferia.
const MAX_FILE_MB = 10

export function AbraMidiaEditPage() {
  const abraMidiaService = new AbraMidiaService()
  const { id } = useParams<{ id: string }>()
  const [selectedFiles, setSelectedFiles] = useState<(File | IUploadedFile)[]>([])
  const [removedFiles, setRemovedFiles] = useState<IUploadedFile[]>([])
  const [form, setForm] = useState<IForm>({
    name: '',
    note: ''
  })
  const [saving, setSaving] = useState(false)
  const [savingMessage, setSavingMessage] = useState('')
  const navigate = useNavigate()
  const toast = useAppToast()

  // A galeria com o cartão de "adicionar" na primeira célula e os arquivos escolhidos nas demais,
  // com as setas de reordenar, é desta tela — um `<FileDropzone>` traria uma segunda lista por
  // baixo dela. Do hook vem o que precisa ser igual em todo lugar: o token, o limite e as recusas.
  const { open, inputProps } = useFilePicker({
    accept: 'media',
    maxSizeMB: MAX_FILE_MB,
    // Só os recém-escolhidos: os já enviados são `IUploadedFile`, e não entram na checagem de
    // duplicata nem de tamanho.
    files: selectedFiles.filter((file): file is File => file instanceof File),
    onPick: (accepted) => setSelectedFiles((prev) => [...prev, ...accepted])
  })

  const moveFilePosition = (curIndex: number, newIndex: number) => {
    const element = selectedFiles.splice(curIndex, 1)[0]

    selectedFiles.splice(newIndex, 0, element)

    setSelectedFiles([...selectedFiles])
  }

  const removeFile = (index: number) => {
    if (!(selectedFiles[index] instanceof File)) {
      setRemovedFiles([...removedFiles, selectedFiles[index] as IUploadedFile])
    }

    selectedFiles.splice(index, 1)

    setSelectedFiles([...selectedFiles])
  }

  const submitForm = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!id) {
      return
    }

    const abraMidiaId = parseInt(id)

    setSaving(true)
    setSavingMessage('Salvando ABRA Mídia')

    try {
      const { data: abraMidia } = await abraMidiaService.update(abraMidiaId, {
        title: form.name,
        notes: form.note
      })

      let position = 1
      for (const file of selectedFiles) {
        setSavingMessage(`Enviando ${file.name}`)

        if (file instanceof File) {
          await abraMidiaService.uploadFile({
            file,
            position: position++,
            abraMediaId: abraMidia.id
          })
        } else {
          await abraMidiaService.updateFile({
            abraMediaId: abraMidiaId,
            fileId: parseInt(file.id),
            position: position++
          })
        }
      }

      for (const file of removedFiles) {
        setSavingMessage(`Removendo ${file.name}`)

        await abraMidiaService.removeFile({ abraMediaId: abraMidiaId, fileId: parseInt(file.id) })
      }

      navigate('/abra-midia')
    } catch (error) {
      // Sem o catch, uma falha no upload deixava o overlay de tela cheia para sempre e o
      // painel inteiro inacessível — não havia como sair da página nem repetir o envio.
      toast.error('Erro ao salvar a ABRA Mídia', error)
    } finally {
      setSaving(false)
      setSavingMessage('')
    }
  }

  useEffect(() => {
    if (!id) {
      navigate('/abra-midia')
      return
    }

    abraMidiaService.get(parseInt(id)).then(({ data }) => {
      setForm({
        name: data.title,
        note: data.notes
      })

      setSelectedFiles(
        data.files.map((file: IAbraMidiaFileRaw) => ({ id: file.id, url: file.file_path, name: file.file_name }))
      )
    })
  }, [])

  return (
    <>
      <BackLink to="/abra-midia" />
      <PageTitle title="Editar ABRA Mídia" mb="5" />
      {/* O envio é multi-arquivo e sequencial, então a mensagem por arquivo é informação real:
          é o que diz ao usuário que a página não travou. O `components/loader` fazia isso com um
          overlay magenta de tela cheia num CSS module (e com uma animação `fadeOut` que não
          existia em keyframes nenhum, portanto morta). */}
      {saving ? (
        <LoadingBlock size="page" label={savingMessage} />
      ) : (
        <form style={{ marginBottom: '50px' }} onSubmit={submitForm}>
          <ActionFab action="save" type="submit" />

          <FormField label="Nome/Título" isRequired mb="5">
            <Input type="text" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
          </FormField>

          <FormField label="Observação" isRequired mb="5">
            <Textarea value={form.note} onChange={(event) => setForm({ ...form, note: event.target.value })} />
          </FormField>

          <Grid templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(3, 1fr)' }} gap={{ base: 4, md: 6 }}>
            <GridItem>
              <Box borderWidth="1px" borderRadius="md" overflow="hidden" boxShadow="md" width="100%" mx="auto">
                <Flex justifyContent="center" alignItems="center" height="200px">
                  <Text fontSize="9xl" fontWeight="bold" textAlign="center" color={'gray.300'}>
                    <Icon name="lni lni-add-files" />
                  </Text>
                </Flex>
                <Box p={4}>
                  <input {...inputProps} />
                  <Flex justifyContent="center">
                    <Button colorScheme="blue" onClick={open}>
                      Adicionar arquivo
                    </Button>
                  </Flex>
                  <Text variant="caption" mt={2}>
                    Formatos aceitos: {FILE_TYPES.media.label}
                  </Text>
                  <Text variant="caption" mb="18px">
                    Tamanho máximo por arquivo: {MAX_FILE_MB}MB
                  </Text>
                </Box>
              </Box>
            </GridItem>

            {selectedFiles.map((file, index) => (
              <GridItem key={index}>
                <Box borderWidth="1px" borderRadius="md" overflow="hidden" boxShadow="md" width="100%" mx="auto">
                  {(() => {
                    if (['mp4'].includes(file.name.split('.').pop() || '')) {
                      return (
                        <Flex justifyContent="center" alignItems="center" height="200px">
                          <Text fontSize="8xl" fontWeight="bold" textAlign="center" color={'gray.500'}>
                            <Icon name="lni lni-video" />
                          </Text>
                        </Flex>
                      )
                    }

                    return (
                      <Flex
                        height="200px"
                        justifyContent="center"
                        alignItems="center"
                        boxShadow="inset 0px 0px 0px 3px rgba(0,0,0,0.1)"
                        backgroundImage={`url(${file instanceof File ? URL.createObjectURL(file) : file.url})`}
                        backgroundSize="cover"
                        backgroundPosition="center"
                        backgroundColor="#FFF"
                      ></Flex>
                    )
                  })()}

                  <Flex justifyContent="space-between" alignItems="top" pl={4}>
                    <Text fontSize="xl" fontWeight="bold" textAlign="center" mt={2}>
                      {file.name}
                    </Text>

                    <FormSelect
                      size="sm"
                      width="25%"
                      value={index}
                      onChange={(value) => moveFilePosition(index, Number(value))}
                      options={selectedFiles.map((_, position) => ({ value: position, label: String(position + 1) }))}
                      hideEmptyOption
                    />
                  </Flex>

                  <Box p={4}>
                    <Flex justifyContent="end" mt={4}>
                      <Button colorScheme="red" size="sm" mt={4} onClick={() => removeFile(index)}>
                        Remover
                      </Button>
                    </Flex>
                  </Box>
                </Box>
              </GridItem>
            ))}
          </Grid>
        </form>
      )}
    </>
  )
}
